import React, { useEffect, useState } from "react";
import { Formik, FieldArray, Field, useFormikContext } from "formik";
import _ from "lodash";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../shared/CustomButton";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";

function AutoSubmit() {
  const { values, submitForm } = useFormikContext();
  useEffect(() => {
    if (values.nationalId && values.nationalId.length === 10) {
      submitForm();
    }
  }, [values.nationalId, submitForm]);

  return null;
}

function PatientDetailForm({
  setChiReqRecord,
  setSystemType,
  isFromList = false,
}) {
  const { t, i18n } = useTranslation();
  const [chiLoading, setChiLoading] = useState(false);
  const navigate = useNavigate();
  const addData = async (values) => {
    setChiLoading(true);
    setSystemType(values.systemType);
    try {
      const token = localStorage.getItem("token");
      const tokenWithoutQuotes = token.replace(/"/g, "");
      const response = await fetch(
        `https://stage.api.connect.supergitsa.com/api/check-insurance?id=${values.nationalId}&type=${values.systemType}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenWithoutQuotes}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        setChiLoading(false);

        // Use the json method to parse the response body as JSON

        const result = await response.json();
        const unescapedString = result?.data?.replace(/\\"/g, '"');

        // Parse the string as JSON

        const jsonObject = JSON.parse(unescapedString);

        if (jsonObject.ApiStatus === "Fail") {
          toast.error(jsonObject.ErrorDescription);
        } else if (jsonObject.ApiStatus === "Success") {
          setChiReqRecord(jsonObject);
          toast.success(t("Data Retrieve Successfully!"));
        }
      } else {
        setChiLoading(false);
        throw new Error("Response Issue");
      }
    } catch (error) {
      setChiLoading(false);
      console.log(error);
    }
  };

  const systemTypes = [
    { id: 1, name: t("Nationals And Residents") },
    { id: 2, name: t("Visitors") },
    { id: 3, name: t("Tourists") },
    { id: 4, name: t("Premium Residents") },
    { id: 5, name: t("Hajj & Umrah Visitors") },
  ];

  const validationSchema = Yup.object().shape({
    systemType: Yup.number().required("System Type is required"),
    nationalId: Yup.string().required("National Id is required"),
  });

  const handleGoBack = () => {
    navigate(-1); // Navigate back to the previous page
  };

  return (
    <>
      <Formik
        initialValues={{
          systemType: 1,
          nationalId: "",
        }}
        enableReinitialize={true}
        validate={(values) => {
          const errors = {};
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          addData(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,

          /* and other goodies */
        }) => (
          <div class="patient-details !mb-2">
            <div className="flex space-x-2 !mb-2">
              <div
                className="back flex items-center justify-center cursor-pointer transition-all duration-200 hover:opacity-80 active:scale-95"
                onClick={handleGoBack}
                style={{
                  backgroundColor: "#0070C2",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                {i18n.language == "en" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                      fill="white"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g transform="scale(-1,1) translate(-24,0)">
                      <path
                        d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                )}
              </div>

              <div className="">
                <Tooltip
                  placement="right"
                  title={t("Keep track of your Beneficiary account")}
                  className="my-auto !mt-2"
                >
                  <span className="font-semibold text-black/70 text-lg ">
                    {t("Create New Beneficiary")}
                    <br />
                  </span>
                </Tooltip>
              </div>
            </div>

            <div class="details-fields grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2">
              <CustomFilterInput
                label={t("System Type*")}
                name="systemType"
                isSelect={true}
                placeholder={t("Select System Type")}
                options={systemTypes}
                value={values.systemType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.systemType}
                touched={touched.systemType}
                hideErrorText={true}
              />
              <CustomFilterInput
                label={t("National ID or Iqama*")}
                name="nationalId"
                placeholder={t("Enter National ID or Iqama")}
                value={values.nationalId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.nationalId}
                touched={touched.nationalId}
                hideErrorText={true}
              />
              <div className="flex items-end">
                <AppButton
                  isLoading={chiLoading}
                  onClick={handleSubmit}
                  startIcon={<PersonSearchIcon />}
                >
                  {t("Inquire CCHI")}
                </AppButton>
              </div>
              <AutoSubmit />
            </div>
          </div>
        )}
      </Formik>
    </>
  );
}

export default PatientDetailForm;
